import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Close as CloseIcon, Search as SearchIcon } from '@mui/icons-material';
import productoService, { PARAMS_PRODUCTO_OPERATIVO, Producto } from '../../services/productoService';
import { formatCurrency } from '../../utils/formatters';
import { obtenerPrecioBaseProducto } from '../../utils/precioListaPedido';

export interface RecetaArticuloSeleccionado {
  codigoArticulo: string;
  descripcion: string;
  unidadMedida: string;
  costo: number;
  precioVenta?: number;
  tipoArticulo?: string | null;
}

interface Props {
  open: boolean;
  title?: string;
  tiposArticulo?: string;
  /** 'venta' muestra precio neto de catálogo; default 'costo'. */
  modoPrecio?: 'costo' | 'venta';
  initialSearchTerm?: string;
  onClose: () => void;
  onSelect: (item: RecetaArticuloSeleccionado) => void;
}

const PAGE_SIZE = 10;

export function mapProductoToRecetaArticulo(p: Producto): RecetaArticuloSeleccionado {
  const um = String(
    (p as { idUnidadMedida?: string; um?: string; unidadMedida?: string }).idUnidadMedida
      ?? (p as { um?: string }).um
      ?? (p as { unidadMedida?: string }).unidadMedida
      ?? '',
  ).trim();
  return {
    codigoArticulo: String(p.codigoArticulo ?? '').trim(),
    descripcion: String(p.descripcion ?? '').trim(),
    unidadMedida: um || '—',
    costo: Number(p.costo ?? 0) || 0,
    precioVenta: obtenerPrecioBaseProducto(p),
    tipoArticulo: p.tipoArticulo ?? null,
  };
}

const RecetaArticuloSearchDialog: React.FC<Props> = ({
  open,
  title = 'Buscar artículo',
  tiposArticulo,
  modoPrecio = 'costo',
  initialSearchTerm = '',
  onClose,
  onSelect,
}) => {
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [items, setItems] = useState<Producto[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await productoService.getProductos({
        searchTerm: searchTerm.trim() || undefined,
        pageNumber: page + 1,
        pageSize: PAGE_SIZE,
        ...PARAMS_PRODUCTO_OPERATIVO,
        ...(tiposArticulo?.trim() ? { tiposArticulo: tiposArticulo.trim() } : {}),
      });
      setItems(res.items ?? []);
      setTotalCount(res.totalCount ?? 0);
    } catch {
      setItems([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, page, tiposArticulo]);

  useEffect(() => {
    if (!open) return;
    setSearchTerm(initialSearchTerm);
    setPage(0);
  }, [open, initialSearchTerm]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => void load(), searchTerm ? 250 : 0);
    return () => clearTimeout(t);
  }, [open, load, searchTerm, page]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <Typography variant="h6" component="span">
          {title}
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label="Cerrar">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            fullWidth
            autoFocus
            label="Buscar por código o descripción"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void load();
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: loading ? (
                <InputAdornment position="end">
                  <CircularProgress size={18} />
                </InputAdornment>
              ) : undefined,
            }}
          />

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Código</TableCell>
                  <TableCell>Descripción</TableCell>
                  <TableCell>U/M</TableCell>
                  <TableCell align="right">{modoPrecio === 'venta' ? 'Precio neto' : 'Costo'}</TableCell>
                  <TableCell align="center" width={120}>
                    Acción
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((p) => {
                  const mapped = mapProductoToRecetaArticulo(p);
                  return (
                    <TableRow key={mapped.codigoArticulo} hover>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{mapped.codigoArticulo}</TableCell>
                      <TableCell>{mapped.descripcion}</TableCell>
                      <TableCell>{mapped.unidadMedida}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(
                          modoPrecio === 'venta'
                            ? mapped.precioVenta ?? mapped.costo
                            : mapped.costo
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            onSelect(mapped);
                            onClose();
                          }}
                        >
                          Seleccionar
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!loading && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Box sx={{ py: 3, color: 'text.secondary' }}>No se encontraron artículos.</Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_e, p) => setPage(p)}
            rowsPerPage={PAGE_SIZE}
            rowsPerPageOptions={[PAGE_SIZE]}
            labelRowsPerPage="Filas"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecetaArticuloSearchDialog;
